import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

const fetchWithTimeout = async (resource, options = {}) => {
    const { timeout = 8000 } = options;

    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    const response = await fetch(resource, {
        ...options,
        signal: controller.signal
    });
    clearTimeout(id);
    return response;
};

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null);

    const refreshUser = async () => {
        try {
            const response = await fetchWithTimeout('/api.php?action=get_user_data', {
                credentials: 'include',
                timeout: 5000
            });
            const data = await response.json();
            if (data.success) {
                setUser(data.user);
                localStorage.setItem('futura_user', JSON.stringify(data.user));
            } else if (response.status === 401) {
                // Session expired on server, clear local
                setUser(null);
                localStorage.removeItem('futura_user');
            }
        } catch (error) {
            console.error("Error refreshing user data:", error);
            if (error.name === 'AbortError') {
                console.warn("Refresh user timeout");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Check local storage for initial fast-load
        const storedUser = localStorage.getItem('futura_user');
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }

        // Always try to sync with server's latest data
        refreshUser();
    }, []);

    const login = async (email, password) => {
        setAuthError(null);
        try {
            const response = await fetchWithTimeout('/api.php?action=login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email, password }),
                timeout: 8000 // 8s timeout as requested
            });

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                const text = await response.text();
                console.error("Server returned non-JSON response:", text);

                // Mensajes específicos según código de estado HTTP
                if (response.status === 401) return { success: false, error: "Correo o contraseña incorrectos" };
                if (response.status === 403) return { success: false, error: "Tu cuenta está suspendida. Contacta soporte." };
                if (response.status === 429) return { success: false, error: "Demasiados intentos. Espera un momento." };
                if (response.status >= 500) return { success: false, error: "Error en el servidor. Intenta más tarde." };

                return { success: false, error: "Error al conectar con el servidor. Verifica tu conexión." };
            }

            const data = await response.json();

            if (data.success) {
                const userData = data.user;
                setUser(userData);
                localStorage.setItem('futura_user', JSON.stringify(userData));
                localStorage.setItem('auth_token', 'session_' + Date.now());
                localStorage.setItem('agent_user', JSON.stringify(userData));
                return { success: true, user: userData };
            } else {
                // Return specific error from backend if available
                return { success: false, error: data.error || data.message || 'Correo o contraseña incorrectos' };
            }
        } catch (error) {
            console.error("Login fetch error:", error);
            if (error.name === 'AbortError') {
                return { success: false, error: "El servidor tardó demasiado en responder. Intenta de nuevo." };
            }
            return { success: false, error: "Error de conexión. Verifica tu internet e intenta de nuevo." };
        }
    };

    const register = async (fullName, email, password) => {
        try {
            const response = await fetch('/api.php?action=register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ full_name: fullName, email, password })
            });
            const data = await response.json();

            if (data.success) {
                setUser(data.user);
                localStorage.setItem('futura_user', JSON.stringify(data.user));
                return { success: true };
            } else {
                return { success: false, error: data.error };
            }
        } catch (error) {
            return { success: false, error: "Error de conexión" };
        }
    };

    const logout = async () => {
        try {
            await fetch('/api.php?action=logout', {
                credentials: 'include'
            });
        } catch (error) {
            console.error("Error during backend logout:", error);
        }
        setUser(null);
        localStorage.removeItem('futura_user');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('agent_user');
    };

    return (
        <AuthContext.Provider value={{ user, login, register, logout, loading, refreshUser, authError }}>
            {children}
        </AuthContext.Provider>
    );
};
